import React, { useId } from 'react';
import { cx } from './cx';

interface FieldExtras {
  /** Com label/hint/error o campo vem embrulhado num <div>; sem eles, é só o controle (drop-in). */
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** Classe do wrapper (só quando há label/hint/error). */
  wrapperClassName?: string;
}

function FieldShell({
  id,
  label,
  hint,
  error,
  required,
  wrapperClassName,
  children,
}: FieldExtras & { id: string; required?: boolean; children: React.ReactNode }) {
  if (!label && !hint && !error) return <>{children}</>;
  return (
    <div className={wrapperClassName}>
      {label && (
        <label htmlFor={id} className="field-label">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="field-hint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

function useFieldIds(id: string | undefined, error: React.ReactNode, hint: React.ReactNode) {
  const auto = useId();
  const fieldId = id || auto;
  const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
  return { fieldId, describedBy };
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & FieldExtras;

/** Campo de texto padrão (.field). Mobile: 44px e 16px pela base do index.css. */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, wrapperClassName, className, id, ...rest }, ref) => {
    const { fieldId, describedBy } = useFieldIds(id, error, hint);
    return (
      <FieldShell id={fieldId} label={label} hint={hint} error={error} required={rest.required} wrapperClassName={wrapperClassName}>
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx('field', Boolean(error) && 'field--error', className)}
          {...rest}
        />
      </FieldShell>
    );
  }
);
Input.displayName = 'Input';

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & FieldExtras;

/** Select padrão (.field + seta). */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, hint, error, wrapperClassName, className, id, children, ...rest }, ref) => {
    const { fieldId, describedBy } = useFieldIds(id, error, hint);
    return (
      <FieldShell id={fieldId} label={label} hint={hint} error={error} required={rest.required} wrapperClassName={wrapperClassName}>
        <select
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx('field field--select', Boolean(error) && 'field--error', className)}
          {...rest}
        >
          {children}
        </select>
      </FieldShell>
    );
  }
);
Select.displayName = 'Select';

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & FieldExtras;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, error, wrapperClassName, className, id, ...rest }, ref) => {
    const { fieldId, describedBy } = useFieldIds(id, error, hint);
    return (
      <FieldShell id={fieldId} label={label} hint={hint} error={error} required={rest.required} wrapperClassName={wrapperClassName}>
        <textarea
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cx('field', Boolean(error) && 'field--error', className)}
          {...rest}
        />
      </FieldShell>
    );
  }
);
Textarea.displayName = 'Textarea';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  type?: 'checkbox' | 'radio';
  /** Classe do <label> (linha de toque inteira). */
  className?: string;
  inputClassName?: string;
}

/**
 * Checkbox padrão: a linha inteira (label) é a área de toque (.check-row, 44px no mobile).
 * Sem `label`, renderiza só o input estilizado (ex.: célula de tabela "selecionar").
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, type = 'checkbox', className, inputClassName, ...rest }, ref) => {
    const input = <input ref={ref} type={type} className={cx('check-input', inputClassName)} {...rest} />;
    if (!label && !description) return input;
    return (
      <label className={cx('check-row', rest.disabled && 'cursor-not-allowed opacity-60', className)}>
        {input}
        <span className="min-w-0">
          {label && <span className="block text-sm text-gray-800">{label}</span>}
          {description && <span className="block text-xs text-gray-500">{description}</span>}
        </span>
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';

export const Radio = React.forwardRef<HTMLInputElement, Omit<CheckboxProps, 'type'>>((props, ref) => (
  <Checkbox ref={ref} type="radio" {...props} />
));
Radio.displayName = 'Radio';
